import { z } from "zod";
/** All enum-ish string unions live here so API and web agree on the exact literals. */
export const UserRole = z.enum(["admin", "student"]);
/**
 * `CODE` is the odd one out: it has no options and no key, and correctness is
 * whether the student's JavaScript passes the question's tests. See `CodeTest`.
 */
export const QuestionType = z.enum(["MCQ", "MSQ", "NAT", "CODE"]);
/** Spelled out — this string is shown verbatim in the admin UI. */
export const Difficulty = z.enum(["easy", "medium", "hard"]);
/**
 * How sure the author is of the ANSWER (not of the question). `medium` means
 * the working is sound but the question as printed is ambiguous or
 * under-determined — only `high` ever counts as a verified key.
 */
export const AnswerConfidence = z.enum(["high", "medium", "low"]);
/**
 * Where a question came from.
 *
 * `pyq` is a past-year *exam* paper. `transcribed` is other real assessment
 * material read off a source document — for this bank, a course's own weekly
 * graded assignments, transcribed from public scans — which is genuine but is
 * not a past-year paper; keeping the two apart stops the admin UI from
 * overstating the bank's provenance.
 */
export const QuestionSource = z.enum([
    "pyq",
    "transcribed",
    "generated",
    "manual",
]);
export const QuestionStatus = z.enum(["pending", "approved", "rejected"]);
export const ExamStatus = z.enum(["draft", "scheduled", "live", "done"]);
/**
 * How an exam's duration relates to the clock.
 *
 * `from_start` — the countdown begins at the attempt's own `started_at` and
 * lasts `duration_min`, wherever that falls inside the availability window.
 * This is the default and the right model for practice: setup time never eats
 * into writing time.
 *
 * `scheduled` — a fixed sitting anchored to `scheduled_at`, for a real
 * invigilated exam. The duration still limits an individual attempt, but the
 * availability window is tied to the scheduled time.
 */
export const ExamTimingMode = z.enum(["from_start", "scheduled"]);
export const AttemptStatus = z.enum([
    "not_started",
    "in_progress",
    "submitted",
    "auto_submitted",
]);
/**
 * A recording track. `webcam` and `screen` are both captured by the laptop
 * (the student's session uploads them); `phone` is the paired side-camera. All
 * three chunk to `attempts/<id>/rec/<source>/<index>.webm` and share the master
 * timeline (`offset_ms` from `attempts.started_at`).
 */
export const RecordingSource = z.enum(["webcam", "phone", "screen"]);
export const AttemptMediaKind = z.enum([
    "paper_check",
    "answer_sheet",
    "room_scan",
]);
/**
 * A `room_scan` is a short **video** (the student pans the phone around the
 * room); `paper_check` and `answer_sheet` are stills. Storage and the admin
 * renderer both branch on this: a video is stored as webm with a video
 * content-type and played in a `<video>` — it must never be served as
 * `image/jpeg` into an `<img>`, which is precisely what made early room scans
 * unplayable.
 */
export const VIDEO_MEDIA_KINDS = [
    "room_scan",
];
export function mediaKindIsVideo(kind) {
    return VIDEO_MEDIA_KINDS.includes(kind);
}
/** The canonical content-type a media kind is stored and served with. */
export function mediaKindContentType(kind) {
    return mediaKindIsVideo(kind) ? "video/webm" : "image/jpeg";
}
/** Object-key extension (no leading dot) for a media kind. */
export function mediaKindExt(kind) {
    return mediaKindIsVideo(kind) ? "webm" : "jpg";
}
export const DeviceRole = z.enum(["laptop", "phone"]);
/**
 * Where the attempt is in the setup/recording flow. The **laptop** owns this
 * value — it is the orchestrator; the phone reads it from its heartbeat and
 * renders whatever stage it is told to.
 */
export const AttemptStage = z.enum([
    "idle",
    "placement",
    "paper_check",
    "room_scan",
    "recording",
    "submitted_upload",
    "done",
]);
/** What the phone should show / capture at each stage. */
export const ATTEMPT_STAGE_LABEL = {
    idle: "Waiting",
    placement: "Position your phone",
    paper_check: "Show your rough sheets",
    room_scan: "Scan the room",
    recording: "Recording",
    submitted_upload: "Uploading answer sheets",
    done: "Finished",
};
/* ------------------------------------------------------------------ */
/* Proctoring                                                          */
/* ------------------------------------------------------------------ */
export const ProctorEventType = z.enum([
    "TAB_SWITCH",
    "WINDOW_BLUR",
    "FULLSCREEN_EXIT",
    "COPY_ATTEMPT",
    "PASTE_ATTEMPT",
    "WEBCAM_STOPPED",
    // The exam is running with NO microphone — refused, absent, or ended
    // mid-exam. Not a violation: plenty of laptops have no working mic, and the
    // student is not asked to fix one mid-paper. It is logged because the
    // ALTERNATIVE is a silent recording with nothing to explain it, and a
    // reviewer who cannot hear the room needs to know that was the equipment
    // rather than the room being quiet.
    "MIC_OFF",
    // The microphone is ON — granted, live, unmuted — and hearing NOTHING. A
    // device muted in the operating system hands the browser a perfectly
    // healthy track that carries digital silence, so every indicator in the app
    // reads green while the file records an hour of nothing. Distinct from
    // MIC_OFF because the two send a reviewer to different places: MIC_OFF is
    // equipment the student does not have, MIC_SILENT is equipment they have
    // and did not switch on.
    "MIC_SILENT",
    "PHONE_DISCONNECTED",
    // Screen share (desktop capture) ended mid-exam — the student revoked the
    // MediaStream or unplugged the display. Serious: no screen track = a blind
    // spot for the rest of the attempt.
    "SCREEN_SHARE_STOPPED",
    // The student granted a single tab/window instead of the whole screen, so
    // the capture misses everything outside it. Client may log this instead of
    // (or before) STOPPED when it can tell the surface is not a monitor.
    "SCREEN_SHARE_NOT_FULL",
    "NO_FACE",
    "MULTI_FACE",
    "NETWORK_DROP",
    // Recorded footage is queued and not reaching the server. Distinct from
    // NETWORK_DROP, which means the browser itself went offline: here the
    // connection is up and simply cannot carry the upload, and the student has
    // been asked to do something about it.
    "UPLOAD_BACKLOG",
    // The laptop stopped beating for long enough that the student is no longer
    // sitting the exam — the tab was closed, the machine slept, the connection
    // died. Emitted by the SERVER on heartbeat lapse, never by the client, which
    // by definition cannot report that it is gone.
    "ATTEMPT_LEFT",
    // ...and it came back. An attempt may legitimately be resumed, so this is
    // not itself a violation; it is the record that there is a gap in the
    // recording and in the timeline, with `away_ms` in the metadata.
    "ATTEMPT_REJOINED",
]);
export const ProctorSeverity = z.enum(["info", "warn", "critical"]);
/**
 * Default severity for each event type. The API stores severity per row so an
 * admin can override, but clients should send this default unless they know
 * better (e.g. repeated TAB_SWITCH escalated to critical).
 */
export const PROCTOR_EVENT_SEVERITY = {
    TAB_SWITCH: "critical",
    WINDOW_BLUR: "warn",
    FULLSCREEN_EXIT: "critical",
    COPY_ATTEMPT: "warn",
    PASTE_ATTEMPT: "critical",
    WEBCAM_STOPPED: "critical",
    // Warn, not critical: it degrades the recording, it does not implicate the
    // student.
    MIC_OFF: "warn",
    // Same reasoning as MIC_OFF: it costs the room audio, it does not
    // implicate the student.
    MIC_SILENT: "warn",
    PHONE_DISCONNECTED: "critical",
    SCREEN_SHARE_STOPPED: "critical",
    SCREEN_SHARE_NOT_FULL: "warn",
    NO_FACE: "warn",
    MULTI_FACE: "critical",
    NETWORK_DROP: "info",
    // Equipment and connection, not conduct.
    UPLOAD_BACKLOG: "info",
    // Leaving mid-exam is worth a look but is not by itself cheating — a
    // power cut looks identical to walking away. The reviewer decides.
    ATTEMPT_LEFT: "warn",
    ATTEMPT_REJOINED: "info",
};
/** Human-readable labels for the admin timeline / student warnings. */
export const PROCTOR_EVENT_LABEL = {
    TAB_SWITCH: "Switched tab",
    WINDOW_BLUR: "Left the exam window",
    FULLSCREEN_EXIT: "Exited fullscreen",
    COPY_ATTEMPT: "Copy attempt",
    PASTE_ATTEMPT: "Paste attempt",
    WEBCAM_STOPPED: "Webcam stopped",
    MIC_OFF: "No microphone — this recording has no sound",
    MIC_SILENT: "Microphone on but silent — muted at the device",
    PHONE_DISCONNECTED: "Phone camera disconnected",
    SCREEN_SHARE_STOPPED: "Screen sharing stopped",
    SCREEN_SHARE_NOT_FULL: "Shared a window, not the whole screen",
    NO_FACE: "No face detected",
    MULTI_FACE: "More than one face detected",
    NETWORK_DROP: "Network dropped",
    UPLOAD_BACKLOG: "Uploads falling behind",
    ATTEMPT_LEFT: "Left the exam",
    ATTEMPT_REJOINED: "Rejoined the exam",
};
/* ------------------------------------------------------------------ */
/* Admin intervention                                                  */
/* ------------------------------------------------------------------ */
/**
 * What an admin did to a live or finished attempt. Every one of these changes
 * a graded record, so each is written to `admin_actions` with the actor and a
 * note rather than applied silently.
 */
export const AdminActionType = z.enum([
    "extend_attempt",
    "extend_exam",
    "force_submit",
    "reopen",
    "reset",
    "grant_retake",
    "edit_exam",
]);
export const ADMIN_ACTION_LABEL = {
    extend_attempt: "Extended this attempt",
    extend_exam: "Extended the exam",
    force_submit: "Ended the attempt",
    reopen: "Reopened the attempt",
    reset: "Reset the attempt",
    grant_retake: "Granted a retake",
    edit_exam: "Edited the exam",
};
//# sourceMappingURL=enums.js.map